import type {
  DayProgress, HealthMetricMap, IsoDate, LogMap, Measurement, QuestRank, WorkoutClass
} from "./types.js";
import { addDays } from "./dates.js";
import type { SeasonBaseline } from "./plan.js";
import { PLAN_START, RACE_DATE_LABEL, TARGET_END_WEIGHT, countdownInfo } from "./plan.js";
import { clamp } from "./numbers.js";
import { STREAK_THRESHOLD, dayCompletion, questRank } from "./scoring.js";
import { latestMeasurement, measurementMap } from "./measurements.js";
import { workoutFor } from "./workout.js";
import type { CheerRow } from "../../shared/types.js";

/** 等級與 EXP 全部由歷史紀錄即時推算，不存 D1。 */
export const XP_PER_TASK = 20;
export const XP_CLEAR_BONUS = 25;
export const XP_PER_MEASUREMENT = 10;
export const XP_PER_LEVEL = 500;

const TITLES = [
  "起跑新手", "配速學徒", "長跑冒險者", "里程戰士",
  "破十勇者", "半馬挑戰者", "半馬完賽者"
];

/**
 * 這裡對量測資料唯一的問題是「那天有沒有量」，不是「量了多少」。
 *
 * 所以型別只要求日期。遮蔽後的 `ShapelessRow` 也塞得進來（見
 * `measurement-view.ts`），於是等級、EXP、連勝與肉羹台詞在監督者那邊照樣
 * 算得對——它們本來就不看數值。
 */
export interface MeasuredDay {
  measurement_date: IsoDate;
}

export interface QuestHistoryRow {
  date: IsoDate;
  progress: DayProgress;
  rank: QuestRank;
  measurement: MeasuredDay | null;
}

export interface HistoryInput {
  logs: LogMap;
  healthMetrics: HealthMetricMap;
  measurements: MeasuredDay[];
}

/** 從計畫起點到指定日期，逐日的完成度與 Rank。 */
export function questHistory(
  { logs, healthMetrics, measurements }: HistoryInput,
  endDate: IsoDate
): QuestHistoryRow[] {
  const byDate = measurementMap(measurements);
  const rows: QuestHistoryRow[] = [];
  for (let date = PLAN_START; date <= endDate; date = addDays(date, 1)) {
    const progress = dayCompletion(logs[date] || {}, healthMetrics[date] || {});
    rows.push({ date, progress, rank: questRank(progress), measurement: byDate[date] || null });
  }
  return rows;
}

export interface PlayerStats {
  xp: number;
  level: number;
  levelXp: number;
  title: string;
  streak: number;
}

export function playerStats(input: HistoryInput, today: IsoDate): PlayerStats {
  const rows = questHistory(input, today);

  let xp = 0;
  for (const row of rows) {
    xp += row.progress.done * XP_PER_TASK;
    if (row.progress.complete) xp += XP_CLEAR_BONUS;
    if (row.measurement) xp += XP_PER_MEASUREMENT;
  }

  const level = Math.floor(xp / XP_PER_LEVEL) + 1;
  const levelXp = xp % XP_PER_LEVEL;
  const title = TITLES[Math.min(TITLES.length - 1, Math.floor((level - 1) / 2))];

  // 今天還沒做完之前，不讓「今天」把昨天以前的連勝提前打斷
  let cursor = rows.length - 1;
  if (rows[cursor]?.date === today && rows[cursor].progress.done < STREAK_THRESHOLD) cursor--;

  let streak = 0;
  for (; cursor >= 0; cursor--) {
    if (rows[cursor].progress.done >= STREAK_THRESHOLD) streak++;
    else break;
  }

  return { xp, level, levelXp, title, streak };
}

export interface BossProgress {
  percent: number;
  label: string;
}

/** 體重 Boss 的血量就是體重達成率，與任務完成度無關。 */
export function weightBossProgress(
  measurements: Measurement[],
  baseline: SeasonBaseline | null
): BossProgress {
  const latest = latestMeasurement(measurements);
  if (!baseline || !latest || !Number.isFinite(latest.weight_kg)) {
    return { percent: 0, label: "等待 InBody 同步" };
  }
  const weight = latest.weight_kg as number;
  const total = baseline.weight - TARGET_END_WEIGHT;
  // 起點已經達標時除數是 0 或負數，直接看現在有沒有達標
  const percent = total > 0
    ? clamp((baseline.weight - weight) / total * 100, 0, 100)
    : (weight <= TARGET_END_WEIGHT ? 100 : 0);
  return { percent, label: `體重 Boss HP：${Math.round(100 - percent)}%` };
}

/** FNV-1a。用來讓同一天固定拿到同一句台詞，而不是每次重繪都亂跳。 */
export function hashText(text: unknown): number {
  let hash = 2166136261;
  for (const char of String(text)) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** 台詞一律是鼓勵性的，不使用羞辱或懲罰性文案。這是產品原則。 */
const NPC_LINES: Record<string, string[]> = {
  S: ["今天全任務 CLEAR！肉羹批准你帥一下。", "S Rank！今天每一格都跑到終點了。", "五星魚乾都不夠形容今天的你。", "今天沒有偷懶，肉羹有看到。"],
  A: ["差一格就滿分，今天已經很穩了。", "A Rank 很漂亮，明天再補一刀。", "不用完美，這個節奏就會贏。", "肉羹點頭：今天有認真。"],
  B: ["主線有推進，再補一個簡單任務吧。", "今天還沒結束，先拿下最容易的一格。", "B Rank 不丟臉，持續才是技能。", "不要一次想太多，下一格就好。"],
  C: ["肉羹開始流汗了，先救一個核心任務。", "今天有點卡，但還來得及翻盤。", "至少把訓練、蛋白質或喝水拿下一項。", "今天不是失敗，只是任務還沒解完。"],
  D: ["先做一件最小的事，今天就不算放棄。", "肉羹幫你把起點守住了，動一下就好。", "重新開始不扣分，連續放棄才可惜。", "今天很累也沒關係，先喝水、再決定下一步。"]
};

const WORKOUT_LINES: Record<WorkoutClass, string> = {
  push: "上半身練一練，跑到後段手臂才擺得動。",
  legs: "腿練穩一點，週六的長跑會謝謝你。",
  run: "今天跑到還能跟肉羹聊天的速度就對了。",
  longrun: "長跑日不比快，慢慢跑完今天的距離就贏了。",
  rest: "休息也是課表，走一走，腿正在偷偷變強。",
  recovery: "今天讓腿放假，肉羹負責盯著你不要偷跑。"
};

export interface NpcDialogue {
  text: string;
  rank: QuestRank;
  workoutClass: WorkoutClass;
  progress: DayProgress;
  streak: number;
}

export interface NpcDialogueOptions extends HistoryInput {
  date: IsoDate;
  today: IsoDate;
  offset?: number;
  /**
   * 當天有誰戳過。可選——不傳的呼叫端行為完全不變。
   *
   * 戳的資料放在這裡而不是 HistoryInput：只有台詞用得到它，
   * 放進去會逼 questHistory 與 playerStats 的每個呼叫端都跟著改。
   */
  cheers?: CheerRow[];
}

/**
 * 台詞裡一次最多念幾個名字，再多就數人頭——名字念完會把那一行擠爆。
 *
 * 匯出是為了讓畫面知道「什麼時候念不完」，那時才另外列出完整名單。
 * 兩邊各寫一個數字的話，改了其中一個就會出現「念得完卻又列一次」。
 */
export const CHEER_NAMES_SHOWN = 2;

/** 戳的台詞。與其他台詞同一條原則：只有正面版本。 */
function cheerLines(names: string[]): string[] {
  if (names.length === 0) return [];
  if (names.length === 1) {
    return [
      `${names[0]} 今天來看過你了。`,
      `${names[0]} 戳了你一下，說繼續。`
    ];
  }
  if (names.length <= CHEER_NAMES_SHOWN) {
    return [
      `${names.join("和")} 今天都來看過你了。`,
      `${names.join("、")} 都在等你把今天做完。`
    ];
  }
  const shown = names.slice(0, CHEER_NAMES_SHOWN).join("、");
  return [
    `${shown} 等 ${names.length} 個人今天來看過你。`,
    `今天有 ${names.length} 個人來看你，包括 ${shown}。`
  ];
}

/**
 * 依當天狀態選一句台詞。同樣的輸入永遠得到同樣的輸出，
 * 使用者按肉羹時才用 offset 換下一句。
 */
export function npcDialogue({
  logs, healthMetrics, measurements, date, today, offset = 0, cheers = []
}: NpcDialogueOptions): NpcDialogue {
  const progress = dayCompletion(logs[date] || {}, healthMetrics[date] || {});
  const rank = questRank(progress);
  const [, , workoutClass] = workoutFor(date);
  const countdown = countdownInfo(date);
  const stats = playerStats({ logs, healthMetrics, measurements }, today);

  // 里程碑台詞會蓋過一般台詞
  const special: string[] = [];
  // 比賽當天那句不管有沒有完賽都要成立；監督者也看得到，所以都不提公斤數
  if (countdown.remaining === 0) special.push("今天就是這段路的終點。不論成績，你都站上來了。");
  else if (countdown.remaining === 1) special.push(`明天 ${RACE_DATE_LABEL}。今天早點睡，鞋子跟號碼布先放好。`);
  else if (countdown.remaining === 7) special.push("最後 7 天，不用補課，也不要臨時極端節食。");
  else if (countdown.remaining === 14) special.push("開始減量了。跑得比較少不是偷懶，是在存腿。");
  else if (countdown.remaining === 30) special.push("再 30 天就站上起跑線，現在每一趟都算數。");
  if (stats.streak === 7) special.push("七連勝達成！肉羹宣布你已經進入節奏。");
  if (stats.streak === 14) special.push("十四連勝！這已經不是運氣，是習慣。");

  const normal = [...(NPC_LINES[rank.grade] || NPC_LINES.D), WORKOUT_LINES[workoutClass]].filter(Boolean);
  const cheerPool = cheerLines(cheers.filter(cheer => cheer.cheer_date === date).map(cheer => cheer.nickname));

  /**
   * 有人戳的時候先講那件事——它是今天唯一「剛剛才發生」的資訊，其餘台詞
   * 隨時都講得到。但後面仍然接著里程碑與一般台詞，按肉羹就換得到。
   *
   * 這種情況下不用 hash 決定起點：hash 的用途是讓同一天固定拿到同一句，
   * 而這裡要的是固定拿到「第一句」。
   */
  if (cheerPool.length) {
    const pool = [...cheerPool, ...special, ...normal];
    return { text: pool[offset % pool.length], rank, workoutClass, progress, streak: stats.streak };
  }

  const pool = special.length ? special : normal;
  const index = (hashText(`${date}|${rank.grade}|${progress.done}`) + offset) % pool.length;
  return { text: pool[index], rank, workoutClass, progress, streak: stats.streak };
}
